import { useState, useCallback, useMemo, useEffect } from 'react'

import {
  Box,
  Paper,
  ButtonProps,
  Collapse,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  MenuItem,
  Typography,
  ExtendButtonBase,
} from '@material-ui/core'

import { toInteger, reduce } from 'lodash'
// MUI or FA?
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'

import { MultiSectionDialog } from '@scoir/modals'

import {
  RoundRequirement,
  academicRecordKeys,
  academicRecordsMap,
  academicRecordKeyMap,
  listOfAcademicRecordKeys,
  supportingDocumentKeys,
  supportingDocumentsMap,
  supportingDocumentKeyMap,
  listOfSupportingRecordKeys,
  transferRequirementKeys,
  transferRequirementsMap,
  transferRequirementKeyMap,
  listOfTransferRequirements,
  docTypesWithPendingRequired,
} from '../types'
import { DOCUMENT_LABELS } from 'common/document.labels.type'
import { ApplicantTypeLabels } from '@scoir/application-info'
import { useRequirements, useUpdateRequirements } from '../RoundRequirementsProvider'

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    maxWidth: '400px',
  },
  checkIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  expandBtn: {
    marginLeft: 'auto',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  checkboxGridItem: {
    alignSelf: 'center',
  },
}))

interface RequirementsModalTriggerProps extends ButtonProps {
  Component: ExtendButtonBase<any> // I want to restrict to Button / IconButton ... or extend ButtonProps
  requirements?: RoundRequirement
  copy?: boolean
}

const RequirementsModalTrigger = ({ Component, requirements, copy, ...rest }: RequirementsModalTriggerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <Component
        onClick={() => setIsOpen(true)}
        {...rest}
      />
      <RequirementModal
        title={copy ? 'Copy Requirements' : requirements ? 'Edit Requirements' : 'Create Requirements'}
        open={isOpen}
        requirements={copy ? ({ ...requirements, id: '', name: '' } as RoundRequirement) : requirements}
        onClose={() => {
          setIsOpen(false)
        }}
      />
    </>
  )
}

const { Body, Footer } = MultiSectionDialog

interface RequirementsModalContentProps {
  requirements?: RoundRequirement
  onClose: () => void
}

const RequirementModalContent = ({ requirements, onClose }: RequirementsModalContentProps) => {
  const classes = useStyles()

  const [editRequirements, setEditRequirements] = useState<RoundRequirement>(
    requirements
      ? { ...requirements }
      : {
          id: '',
          applicantType: undefined,
          name: '',
          updatedBy: '',
          updatedAt: '',
          updatedByName: '',
          recommendations: {
            min: 0,
            max: 0,
            teacher: {
              RequiredCount: 0,
              OptionalSendStatus: 'DoNotSend',
            },
            counselor: {
              RequiredCount: 0,
              OptionalSendStatus: 'DoNotSend',
            },
            other: {
              RequiredCount: 0,
              OptionalSendStatus: 'DoNotSend',
            },
          },
          academicRecords: reduce(
            academicRecordKeyMap,
            (reducerValue: academicRecordsMap, key: academicRecordKeys) => {
              reducerValue[key] = key === 'InitialTranscript' ? 'Required' : 'AsAvailable'
              return reducerValue
            },
            {},
          ),
          supportingDocuments: reduce(
            supportingDocumentKeyMap,
            (reducerValue: supportingDocumentsMap, key: supportingDocumentKeys) => {
              reducerValue[key] = key === 'SecondarySchoolReport' ? 'Required' : 'AsAvailable'
              return reducerValue
            },
            {},
          ),
          transferRequirements: reduce(
            transferRequirementKeyMap,
            (reducerValue: transferRequirementsMap, key: transferRequirementKeys) => {
              reducerValue[key] = 'DoNotSend'
              return reducerValue
            },
            {},
          ),
        },
  )

  const allRequirements = useRequirements()
  const allRequirementNames = useMemo(() => allRequirements.map((r) => r.name), [allRequirements])

  const updateAcademicRecord = useCallback(
    (key, value) =>
      setEditRequirements({
        ...editRequirements,
        academicRecords: {
          ...editRequirements.academicRecords,
          [key]: value,
        },
      }),
    [editRequirements],
  )

  const updateSupportingRecord = useCallback(
    (key, value) =>
      setEditRequirements({
        ...editRequirements,
        supportingDocuments: {
          ...editRequirements.supportingDocuments,
          [key]: value,
        },
      }),
    [editRequirements],
  )

  const updateTransferRequirement = useCallback(
    (key, value) =>
      setEditRequirements({
        ...editRequirements,
        transferRequirements: {
          ...editRequirements.transferRequirements,
          [key]: value,
        },
      }),
    [editRequirements],
  )

  const [missingRequirements, setMissingRequirements] = useState<Record<string, boolean>>(
    requirements
      ? {
          name: requirements.name ? false : true,
        }
      : {
          name: true,
          applicantType: true,
          recMin: false,
          recMax: false,
        },
  )
  const updateMissingRequirements = useCallback(
    (updates) => {
      setMissingRequirements({ ...missingRequirements, ...updates })
    },
    [missingRequirements],
  )

  const [invalid, setInvalid] = useState<Record<string, string | boolean>>({})
  const updateInvalid = useCallback(
    (invalidUpdates: Record<string, string | boolean>) => {
      setInvalid({
        ...invalid,
        ...invalidUpdates,
      })
    },
    [invalid],
  )
  const isInvalid = useMemo(() => Object.keys(invalid).find((key) => !!invalid[key]), [invalid])
  const isMissingRequirements = useMemo(
    () => !!Object.keys(missingRequirements).find((key) => !!missingRequirements[key]),
    [missingRequirements],
  )
  const updateRequirements = useCallback(
    (updates, requirementUpdates?) => {
      const updatedRound = { ...editRequirements, ...updates }
      setEditRequirements(updatedRound)
      updateMissingRequirements(requirementUpdates)
    },
    [editRequirements, updateMissingRequirements],
  )

  const submitRound = useUpdateRequirements()
  const [errorMessage, setErrorMessage] = useState('')

  const [expandContainer, setExpandContainers] = useState({
    details: true,
    recommendations: true,
    documents: true,
  })
  useEffect(() => {
    const cousnelor = editRequirements.recommendations.counselor.RequiredCount ?? 0
    const teacher = editRequirements.recommendations.teacher.RequiredCount ?? 0
    const other = editRequirements.recommendations.other.RequiredCount ?? 0
    const min = editRequirements.recommendations.min ?? 0
    const max = editRequirements.recommendations.max ?? 0
    const newSum = cousnelor + teacher + other
    const hasAnyOptional =
      editRequirements.recommendations.teacher.OptionalSendStatus === 'AsAvailable' ||
      editRequirements.recommendations.other.OptionalSendStatus === 'AsAvailable' ||
      editRequirements.recommendations.counselor.OptionalSendStatus === 'AsAvailable'
    const recMin = min < newSum ? "Total minimum must not be less than the sum of each type's minimum" : ''
    const recMax =
      min === max
        ? ''
        : hasAnyOptional && min <= max
          ? ''
          : 'Total maximum must equal minimum unless allowing for additional recommendations'
    const recTypeReq =
      newSum > max
        ? "Sum of each type's minimum must not be greater than the maximum"
        : !hasAnyOptional && newSum < min
          ? "Sum of each type's minimum must equal total minimum if not allowing additional"
          : ''
    if (recMin !== invalid.recMin || recTypeReq !== invalid.recTypeReq || recMax !== invalid.recMax) {
      updateInvalid({
        recMin,
        recTypeReq,
        recMax,
      })
    }
  }, [invalid, editRequirements, updateInvalid])
  return (
    <>
      <Body>
        <form
          id="requirements"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            setErrorMessage('')
            submitRound({
              ...editRequirements,
              name: editRequirements.name.trim(),
            })
              .then(onClose)
              .catch((errResponse) => {
                return errResponse.status === 409
                  ? 'A Requirements record with that name already exists.'
                  : errResponse.text()
              })
              .then((errorMessage) => setErrorMessage(errorMessage))
          }}
        >
          <Box mb={2}>
            <Paper elevation={0}>
              <Box p={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Box mr={1}>
                    <Typography
                      component="h3"
                      variant="h6"
                    >
                      Details
                    </Typography>
                  </Box>
                  <IconButton
                    className={`${classes.expandBtn} ${expandContainer.details ? classes.expandOpen : classes.expand}`}
                    onClick={() => setExpandContainers({ ...expandContainer, details: !expandContainer.details })}
                    aria-expanded={expandContainer.details}
                    aria-label={expandContainer.details ? 'Close Details Section' : 'Open Details Section'}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={expandContainer.details}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <TextField
                      fullWidth
                      required
                      margin="normal"
                      value={editRequirements.name}
                      error={!!invalid.name}
                      helperText={invalid.name ? 'This name conflicts with an existing requirements name' : ''}
                      FormHelperTextProps={{ role: 'alert' }}
                      label="Requirements Name"
                      id="name"
                      onChange={(e) => {
                        const name = e.target.value
                        updateRequirements(
                          { name },
                          { name: !name || allRequirementNames.findIndex((rName) => rName === name) !== -1 },
                        )
                        updateInvalid({ name: allRequirementNames.findIndex((rName) => rName === name) !== -1 })
                      }}
                      variant="outlined"
                    />
                    <TextField
                      select
                      required
                      label="Applicant Type"
                      id="applicant-type"
                      margin="normal"
                      value={editRequirements.applicantType ?? ''}
                      disabled={requirements?.id ? true : false}
                      onChange={(e) => {
                        const applicantType = e.target.value
                        updateRequirements(
                          {
                            applicantType,
                            transferRequirements: reduce(
                              transferRequirementKeyMap,
                              (reducerValue: transferRequirementsMap, key: transferRequirementKeys) => {
                                reducerValue[key] = 'DoNotSend'
                                return reducerValue
                              },
                              {},
                            ),
                            academicRecords: reduce(
                              academicRecordKeyMap,
                              (reducerValue: academicRecordsMap, key: academicRecordKeys) => {
                                reducerValue[key] =
                                  applicantType === 'transfer'
                                    ? 'DoNotSend'
                                    : key === 'InitialTranscript'
                                      ? 'Required'
                                      : 'AsAvailable'
                                return reducerValue
                              },
                              {},
                            ),
                            supportingDocuments: reduce(
                              supportingDocumentKeyMap,
                              (reducerValue: supportingDocumentsMap, key: supportingDocumentKeys) => {
                                reducerValue[key] =
                                  applicantType === 'transfer'
                                    ? 'DoNotSend'
                                    : key === 'SecondarySchoolReport'
                                      ? 'Required'
                                      : 'AsAvailable'
                                return reducerValue
                              },
                              {},
                            ),
                          },
                          { applicantType: !applicantType },
                        )
                      }}
                      variant="outlined"
                      fullWidth
                    >
                      <MenuItem value="firstYear">{ApplicantTypeLabels.firstYear}</MenuItem>
                      <MenuItem value="transfer">{ApplicantTypeLabels.transfer}</MenuItem>
                    </TextField>
                  </Box>
                </Collapse>
              </Box>
            </Paper>
          </Box>
          <Box mb={2}>
            <Paper elevation={0}>
              <Box p={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Box mr={1}>
                    <Typography
                      component="h3"
                      variant="h6"
                    >
                      Recommendations
                    </Typography>
                  </Box>
                  <IconButton
                    className={`${classes.expandBtn} ${
                      expandContainer.recommendations ? classes.expandOpen : classes.expand
                    }`}
                    onClick={() =>
                      setExpandContainers({ ...expandContainer, recommendations: !expandContainer.recommendations })
                    }
                    aria-expanded={expandContainer.recommendations}
                    aria-label={expandContainer.recommendations ? 'Close Details Section' : 'Open Details Section'}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Box my={1}>
                  <Collapse in={expandContainer.recommendations}>
                    <Grid
                      container
                      spacing={2}
                      alignItems="flex-start"
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth
                          required
                          error={!!invalid.recMin}
                          FormHelperTextProps={{ role: 'alert' }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText={invalid.recMin}
                          value={editRequirements.recommendations.min}
                          label="Minimum"
                          id="minimum"
                          type="number"
                          onChange={(e) => {
                            const min = toInteger(e.target.value)
                            const recommendations = { ...editRequirements.recommendations, min }
                            updateRequirements({ recommendations }, { recMin: min < 0 })
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth
                          required
                          error={!!invalid.recMax}
                          FormHelperTextProps={{ role: 'alert' }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText={invalid.recMax}
                          value={editRequirements.recommendations.max}
                          label="Maximum"
                          id="maximum"
                          type="number"
                          onChange={(e) => {
                            const max = toInteger(e.target.value)
                            const recommendations = { ...editRequirements.recommendations, max }
                            updateRequirements({ recommendations }, { recMax: max < 0 })
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth
                          required
                          error={!!invalid.recTypeReq || editRequirements.recommendations.counselor.RequiredCount < 0}
                          FormHelperTextProps={{ role: 'alert' }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText={
                            editRequirements.recommendations.counselor.RequiredCount < 0 ? 'Cannot be negative' : ''
                          }
                          value={editRequirements.recommendations.counselor.RequiredCount}
                          label="Counselor Recs"
                          type="number"
                          id="counselor-min"
                          onChange={(e) => {
                            const RequiredCount = toInteger(e.target.value)
                            const newCounselor = { ...editRequirements.recommendations.counselor, RequiredCount }
                            const recommendations = { ...editRequirements.recommendations, counselor: newCounselor }
                            updateRequirements({ recommendations }, { counselorRecs: RequiredCount < 0 })
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.checkboxGridItem}
                      >
                        <FormControlLabel
                          label="Allow Additional"
                          id="counselor-additional"
                          control={
                            <Checkbox
                              disableRipple={false}
                              checked={editRequirements.recommendations.counselor.OptionalSendStatus === 'AsAvailable'}
                              onChange={(e) => {
                                const OptionalSendStatus = e.target.checked ? 'AsAvailable' : 'DoNotSend'
                                const newCounselor = {
                                  ...editRequirements.recommendations.counselor,
                                  OptionalSendStatus,
                                }
                                const recommendations = { ...editRequirements.recommendations, counselor: newCounselor }
                                updateRequirements({ recommendations })
                              }}
                              name="awaiting"
                              color="primary"
                            />
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth
                          required
                          error={!!invalid.recTypeReq || editRequirements.recommendations.teacher.RequiredCount < 0}
                          FormHelperTextProps={{ role: 'alert' }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText={
                            editRequirements.recommendations.teacher.RequiredCount < 0 ? 'Cannot be negative' : ''
                          }
                          value={editRequirements.recommendations.teacher.RequiredCount}
                          label="Teacher Recs"
                          id="teacher-min"
                          type="number"
                          onChange={(e) => {
                            const RequiredCount = toInteger(e.target.value)
                            const newTeacher = { ...editRequirements.recommendations.teacher, RequiredCount }
                            const recommendations = { ...editRequirements.recommendations, teacher: newTeacher }
                            updateRequirements({ recommendations }, { teacherRecs: RequiredCount < 0 })
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.checkboxGridItem}
                      >
                        <FormControlLabel
                          label="Allow Additional"
                          id="teacher-additional"
                          control={
                            <Checkbox
                              disableRipple={false}
                              checked={editRequirements.recommendations.teacher.OptionalSendStatus === 'AsAvailable'}
                              onChange={(e) => {
                                const OptionalSendStatus = e.target.checked ? 'AsAvailable' : 'DoNotSend'
                                const newTeacher = { ...editRequirements.recommendations.other, OptionalSendStatus }
                                const recommendations = { ...editRequirements.recommendations, teacher: newTeacher }
                                updateRequirements({ recommendations })
                              }}
                              name="awaiting"
                              color="primary"
                            />
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth
                          required
                          error={!!invalid.recTypeReq || editRequirements.recommendations.other.RequiredCount < 0}
                          FormHelperTextProps={{ role: 'alert' }}
                          inputProps={{ min: 0, step: 1 }}
                          helperText={
                            editRequirements.recommendations.other.RequiredCount < 0 ? 'Cannot be negative' : ''
                          }
                          value={editRequirements.recommendations.other.RequiredCount}
                          label="Other Recs"
                          id="other-min"
                          type="number"
                          onChange={(e) => {
                            const RequiredCount = toInteger(e.target.value)
                            const newOther = { ...editRequirements.recommendations.other, RequiredCount }
                            const recommendations = { ...editRequirements.recommendations, other: newOther }
                            updateRequirements({ recommendations }, { otherRecs: RequiredCount < 0 })
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.checkboxGridItem}
                      >
                        <FormControlLabel
                          label="Allow Additional"
                          id="other-additional"
                          control={
                            <Checkbox
                              disableRipple={false}
                              checked={editRequirements.recommendations.other.OptionalSendStatus === 'AsAvailable'}
                              onChange={(e) => {
                                const OptionalSendStatus = e.target.checked ? 'AsAvailable' : 'DoNotSend'
                                const newOther = { ...editRequirements.recommendations.other, OptionalSendStatus }
                                const recommendations = { ...editRequirements.recommendations, other: newOther }
                                updateRequirements({ recommendations })
                              }}
                              name="awaiting"
                              color="primary"
                            />
                          }
                        />
                      </Grid>
                    </Grid>
                    {invalid.recTypeReq && (
                      <Box
                        my={2}
                        textAlign="center"
                      >
                        <Typography
                          role="alert"
                          color="error"
                        >
                          {invalid.recTypeReq}
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </Box>
              </Box>
            </Paper>
          </Box>
          {editRequirements.applicantType && (
            <Box>
              <Paper elevation={0}>
                <Box p={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Box mr={1}>
                      <Typography
                        component="h3"
                        variant="h6"
                      >
                        Documents
                      </Typography>
                    </Box>
                    <IconButton
                      className={`${classes.expandBtn} ${
                        expandContainer.documents ? classes.expandOpen : classes.expand
                      }`}
                      onClick={() => setExpandContainers({ ...expandContainer, documents: !expandContainer.documents })}
                      aria-expanded={expandContainer.documents}
                      aria-label={expandContainer.documents ? 'Close Documents Section' : 'Open Documents Section'}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                    {editRequirements.applicantType === 'firstYear' && (
                      <Box mb={2}>
                        <Alert role='note' severity="info">
                          New: mid year and final reports/transcripts can now be Required. These docs won’t be included
                          in determining if an app is complete, but school officials will know to send them when they’re
                          ready.
                        </Alert>
                      </Box>
                    )}
                  </Box>
                  <Box my={1}>
                    <Collapse in={expandContainer.documents}>
                      <Grid
                        container
                        spacing={2}
                        alignItems="flex-start"
                      >
                        {editRequirements.applicantType === 'firstYear' &&
                          listOfAcademicRecordKeys.map((key) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              key={key}
                            >
                              <TextField
                                select
                                required
                                label={DOCUMENT_LABELS[key]}
                                value={editRequirements.academicRecords[key]}
                                onChange={(e) => {
                                  updateAcademicRecord(key, e.target.value)
                                }}
                                id={`academic-record-${key}`}
                                variant="outlined"
                                fullWidth
                              >
                                {key === 'InitialTranscript' && <MenuItem value="Required">Required</MenuItem>}
                                {docTypesWithPendingRequired.includes(key) && (
                                  <MenuItem value="PendingRequired">Required</MenuItem>
                                )}
                                <MenuItem value="AsAvailable">As Available</MenuItem>
                                <MenuItem value="DoNotSend">Do Not Send</MenuItem>
                              </TextField>
                            </Grid>
                          ))}
                        {editRequirements.applicantType === 'firstYear' &&
                          listOfSupportingRecordKeys.map((key) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              key={key}
                            >
                              <TextField
                                select
                                required
                                label={DOCUMENT_LABELS[key]}
                                value={editRequirements.supportingDocuments[key]}
                                onChange={(e) => {
                                  updateSupportingRecord(key, e.target.value)
                                }}
                                id={`supporting-document-${key}`}
                                variant="outlined"
                                fullWidth
                              >
                                {key === 'SecondarySchoolReport' && <MenuItem value="Required">Required</MenuItem>}
                                <MenuItem value="AsAvailable">As Available</MenuItem>
                                <MenuItem value="DoNotSend">Do Not Send</MenuItem>
                              </TextField>
                            </Grid>
                          ))}
                        {editRequirements.applicantType === 'transfer' &&
                          listOfTransferRequirements.map((key) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              key={key}
                            >
                              <TextField
                                select
                                required
                                label={DOCUMENT_LABELS[key]}
                                value={editRequirements.transferRequirements[key]}
                                onChange={(e) => {
                                  updateTransferRequirement(key, e.target.value)
                                }}
                                id={`transfer-requirement-${key}`}
                                variant="outlined"
                                fullWidth
                              >
                                <MenuItem value="Required">Required</MenuItem>
                                <MenuItem value="AsAvailable">As Available</MenuItem>
                                <MenuItem value="DoNotSend">Do Not Send</MenuItem>
                              </TextField>
                            </Grid>
                          ))}
                      </Grid>
                    </Collapse>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </form>
      </Body>
      {errorMessage && (
        <Box
          p={2}
          textAlign="center"
        >
          <Typography
            role="alert"
            color="error"
          >
            {errorMessage}
          </Typography>
        </Box>
      )}
      <Footer>
        <Footer.CloseButton>Cancel</Footer.CloseButton>
        <Footer.PrimaryActionButton
          form="requirements"
          disabled={!!isInvalid || isMissingRequirements}
          type="submit"
        >
          {editRequirements.id ? 'Save' : 'Create'}
        </Footer.PrimaryActionButton>
      </Footer>
    </>
  )
}

interface RequirementModalProps extends RequirementsModalContentProps {
  title: string
  open: boolean
}

const RequirementModal = ({ open, onClose, title, ...rest }: RequirementModalProps) => (
  <MultiSectionDialog
    open={open}
    onClose={onClose}
    scroll="paper"
    headerText={title}
  >
    <RequirementModalContent
      onClose={onClose}
      {...rest}
    />
  </MultiSectionDialog>
)
export default RequirementsModalTrigger
